import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Alert,
  Container,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import { parse, parseISO, format, addDays, setHours, setMinutes, setSeconds } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../../services/apiClient';
import { getOrders, updateOrder } from '../../services/orderService';
import { getCourses } from '../../services/settings';
import { useBusinessDate } from '../../hooks/useBusinessDate';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { extractOrders, normalizeOptionDetails } from './castOrderUtils';

/**
 * コース料金表の1件分。
 * コースは order 側では course_duration(分) で識別されるため、duration をキーに使う。
 */
interface CourseOption {
  duration: number;   // 分（コースの識別子として使う）
  label: string;      // 表示名（例: 60分）
  castback: number;   // 料金表上のキャストバック
  revenue: number;    // 料金表上の収益
}

interface OptionChoice {
  id: number | string;
  name: string;
  price: number;
  castback: number;
}

/**
 * オプション詳細の1行。管理者画面(CreateOrder/OrderDetail)と同じ構造。
 * name: オプション名 / castback: キャストバック / profit: 収益
 */
interface OptionDetail {
  id?: number | string;
  name: string;
  castback: number;
  profit: number;
}

/**
 * キャストユーザー専用のオーダー編集画面。
 * 表示・編集できる項目はキャスト向けに限定する:
 *  - コース（変更可）
 *  - キャストバック（編集不可・自動計算）
 *  - 総額（編集不可・自動計算）
 *  - オプション
 *  - 特記事項
 *
 * コース・オプションを変更すると、料金表(courses/options)に準じてキャストバックと総額を
 * 再計算する。ただし割引やオーダー作成時の手動調整分を失わないよう、初期ロード時に
 * ベース額（現状金額からコース分・オプション分を差し引いた残り＝調整分）を算出し、
 * 以降は「ベース額 + 選択コース + 選択オプション」で再計算する。
 *
 * 注意: バックエンドに個別取得(GET /orders/:id)のエンドポイントが無いため、
 * 一覧取得(getOrders)の結果から対象オーダーを取り出す。
 */
const CastOrderEdit: React.FC = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { businessDate, businessHours } = useBusinessDate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [courseOptions, setCourseOptions] = useState<CourseOption[]>([]);
  const [optionChoices, setOptionChoices] = useState<OptionChoice[]>([]);

  // 元のオーダーデータ（送信時にそのまま引き継ぐ）
  const [originalOrder, setOriginalOrder] = useState<any | null>(null);

  // 編集対象の値（コースは duration で保持）
  const [selectedDuration, setSelectedDuration] = useState<number | ''>('');
  // オプションは管理者同様 option_details 配列を正とする。
  // チェックボックス選択・自由入力の両方がこの配列に反映され、金額もここから計算する。
  const [optionDetails, setOptionDetails] = useState<OptionDetail[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState<string>('');
  const [entryTime, setEntryTime] = useState<string>(''); // 入室時間（HH:MM）
  const [serviceTime, setServiceTime] = useState<number>(0); // サービスタイム（追加時間・分）

  // ベース額: 現状の金額から「コース分」「オプション分」を差し引いた残り。
  const [baseCastback, setBaseCastback] = useState<number>(0);
  const [baseRevenue, setBaseRevenue] = useState<number>(0);

  // duration からコース料金表を引く
  const findCourse = useCallback(
    (duration: number | ''): CourseOption | undefined =>
      courseOptions.find((c) => c.duration === Number(duration)),
    [courseOptions]
  );

  // オプション詳細の合計（castback / profit）を算出
  const optionTotals = useMemo(() => {
    return optionDetails.reduce(
      (acc, d) => {
        acc.castback += Number(d.castback) || 0;
        acc.profit += Number(d.profit) || 0;
        return acc;
      },
      { castback: 0, profit: 0 }
    );
  }, [optionDetails]);

  // 現在の金額 = ベース額 + 選択コース + オプション詳細合計
  const selectedCourse = findCourse(selectedDuration);
  const castback = baseCastback + (selectedCourse?.castback || 0) + optionTotals.castback;
  const revenue = baseRevenue + (selectedCourse?.revenue || 0) + optionTotals.profit;
  const grandTotal = castback + revenue;

  // 業務日に基づく検索範囲（一覧APIの start_date/end_date 用）
  const computeDateRange = useCallback(() => {
    const base = businessDate || new Date();
    if (!businessHours) {
      // 営業時間が無い場合は当日0時〜翌日0時で広めに取得
      const start = setSeconds(setMinutes(setHours(base, 0), 0), 0);
      const end = addDays(start, 1);
      return { start, end };
    }
    const startTime = parse(businessHours.start, 'HH:mm', new Date());
    const endTime = parse(businessHours.end, 'HH:mm', new Date());
    const start = setSeconds(setMinutes(setHours(base, startTime.getHours()), startTime.getMinutes()), 0);
    let end;
    if (endTime < startTime) {
      const nextDay = addDays(base, 1);
      end = setSeconds(setMinutes(setHours(nextDay, endTime.getHours()), endTime.getMinutes()), 0);
    } else {
      end = setSeconds(setMinutes(setHours(base, endTime.getHours()), endTime.getMinutes()), 0);
    }
    return { start, end };
  }, [businessDate, businessHours]);

  useEffect(() => {
    const initialize = async () => {
      if (!orderId) {
        setError('オーダーIDが指定されていません。');
        setLoading(false);
        return;
      }

      try {
        // コース料金表を取得（キャストでも閲覧できる /system/courses を使用）。
        // 失敗しても画面は表示する（金額の差分計算ができないだけ）。
        let loadedCourses: CourseOption[] = [];
        try {
          const courses = await getCourses();
          loadedCourses = (courses || []).map((c: any) => ({
            duration: Number(c.duration),
            label: `${c.duration}分`,
            castback: Number(c.castback) || 0,
            revenue: Number(c.revenue) || 0,
          }));
          setCourseOptions(loadedCourses);
        } catch (e) {
          console.warn('コース料金表の取得に失敗しました（権限不足の可能性）:', e);
        }

        // オプション一覧を取得（失敗は許容）
        let loadedOptions: OptionChoice[] = [];
        try {
          const optionsResponse = await api.get('/system/options');
          if (Array.isArray(optionsResponse.data)) {
            loadedOptions = optionsResponse.data;
            setOptionChoices(loadedOptions);
          }
        } catch (e) {
          console.warn('オプション一覧の取得に失敗しました（権限不足の可能性）:', e);
        }

        // 個別取得APIが無いため、一覧から対象オーダーを探す
        const { start, end } = computeDateRange();
        const response = await getOrders({
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        });
        const list = extractOrders(response);
        let order = list.find((o: any) => String(o.id) === String(orderId));

        // 業務日の範囲外にあるオーダーの可能性もあるため、見つからなければ広めに再取得
        if (!order) {
          const wide = await getOrders({
            start_date: addDays(start, -3).toISOString(),
            end_date: addDays(end, 3).toISOString(),
          });
          order = extractOrders(wide).find((o: any) => String(o.id) === String(orderId));
        }

        if (!order) {
          setError('指定されたオーダーが見つかりません。');
          setLoading(false);
          return;
        }

        setOriginalOrder(order);

        const duration = order.course_duration ? Number(order.course_duration) : '';
        setSelectedDuration(duration);
        setServiceTime(Number(order.service_time) || 0);
        setSpecialInstructions(order.special_instructions || order.notes || '');

        // 入室時間（HH:MM）。entry_time_value を優先し、無ければ entry_time(ISO) から抽出
        if (order.entry_time_value) {
          setEntryTime(order.entry_time_value);
        } else if (order.entry_time) {
          try {
            setEntryTime(format(parseISO(order.entry_time), 'HH:mm'));
          } catch {
            setEntryTime('');
          }
        }

        // 現状の金額
        const currentCastback = Number(order.castback) || 0;
        const currentRevenue = Number(order.revenue) || 0;

        // 初期コースの料金表上の金額（差し引く対象）
        const initialCourse = loadedCourses.find((c) => c.duration === Number(duration));
        const initialCourseCastback = initialCourse?.castback || 0;
        const initialCourseRevenue = initialCourse?.revenue || 0;

        // 初期オプションの金額（option_details から合計）。
        // option_details はJSON文字列で保存されている場合があるため正規化する。
        const details = normalizeOptionDetails(order.option_details);
        const initialOptionCastback = details.reduce((sum, d) => sum + (Number(d.castback) || 0), 0);
        const initialOptionProfit = details.reduce((sum, d) => sum + (Number(d.profit) || 0), 0);

        // ベース額 = 現状 − コース分 − オプション分（＝割引等の調整分が残る）
        setBaseCastback(currentCastback - initialCourseCastback - initialOptionCastback);
        setBaseRevenue(currentRevenue - initialCourseRevenue - initialOptionProfit);

        // オプション詳細を初期化（管理者同様に name/castback/profit を保持）
        setOptionDetails(details);
      } catch (err) {
        console.error('オーダー編集画面の初期化に失敗しました:', err);
        setError('データの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // チェックボックスのオプション選択。optionDetails への追加・削除として扱う。
  const handleOptionToggle = (checked: boolean, option: OptionChoice) => {
    if (checked) {
      setOptionDetails((prev) => [
        ...prev,
        {
          id: option.id,
          name: option.name,
          castback: Number(option.castback) || 0,
          profit: (Number(option.price) || 0) - (Number(option.castback) || 0),
        },
      ]);
    } else {
      setOptionDetails((prev) => prev.filter((d) => d.name !== option.name));
    }
  };

  // オプション詳細の各フィールドを更新
  const handleDetailChange = (index: number, field: keyof OptionDetail, value: string) => {
    setOptionDetails((prev) => {
      const next = [...prev];
      if (field === 'name') {
        next[index] = { ...next[index], name: value };
      } else {
        next[index] = { ...next[index], [field]: parseInt(value, 10) || 0 };
      }
      return next;
    });
  };

  // オプション詳細の行を追加
  const handleAddDetail = () => {
    setOptionDetails((prev) => [...prev, { name: '', castback: 0, profit: 0 }]);
  };

  // オプション詳細の行を削除
  const handleRemoveDetail = (index: number) => {
    setOptionDetails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!orderId || !originalOrder) {
      toast.error('オーダー情報が読み込まれていません。');
      return;
    }

    setSaving(true);
    try {
      // 名前が空のオプション詳細は送信から除外する
      const cleanedDetails = optionDetails
        .filter((d) => d.name && d.name.trim() !== '')
        .map((d) => ({
          id: d.id,
          name: d.name,
          castback: Number(d.castback) || 0,
          profit: Number(d.profit) || 0,
        }));

      // キャストが編集した項目「だけ」を送信する。
      // originalOrder を丸ごと送ると user_id(作成者ID)が混入し、バックエンドで
      // cast_ids(担当キャスト)が作成者IDに上書きされてしまう。そのため送信対象を限定する。
      // バックエンドは 'キー' in data のときだけ更新するため、送らない項目は既存値が維持される。
      // 入室時間が入力されている場合は「完了」オーダーとして扱い、一覧に残す。
      const orderData: Record<string, any> = {
        course_duration: selectedDuration === '' ? null : Number(selectedDuration),
        service_time: serviceTime,
        option_details: cleanedDetails,
        special_instructions: specialInstructions,
        entry_time: entryTime || null,
      };
      if (entryTime) {
        orderData.status = '完了';
      }

      const result = await updateOrder(orderId, orderData);
      if (result && result.error) {
        throw new Error(result.message || '更新に失敗しました');
      }

      toast.success('オーダーを更新しました');
      navigate('/orders');
    } catch (err: any) {
      console.error('オーダー更新に失敗しました:', err);
      const message = err?.response?.data?.message || err?.message || '不明なエラー';
      toast.error(`オーダーの更新に失敗しました: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="オーダー編集">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="オーダー編集">
        <Container maxWidth="sm" sx={{ py: 2 }}>
          <Alert severity="error">{error}</Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/orders')}>
            一覧へ戻る
          </Button>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="オーダー編集">
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography variant="h5" gutterBottom>
          オーダー編集
        </Typography>

        <Paper elevation={3} sx={{ p: 2.5, mb: 2 }}>
          <Grid container spacing={2}>
            {/* コース（変更可） */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>コース</InputLabel>
                <Select
                  value={selectedDuration === '' ? '' : String(selectedDuration)}
                  label="コース"
                  onChange={(e) => setSelectedDuration(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  {courseOptions.map((course) => (
                    <MenuItem key={course.duration} value={String(course.duration)}>
                      {course.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* オプション（トグルメニュー） */}
            <Grid item xs={12}>
              <Accordion defaultExpanded={optionDetails.some((d) => d.name && d.name.trim() !== '')}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">オプション</Typography>
                    {/* 選択されているオプション数をバッジ(丸数字)で表示して、
                        オプションが設定されていることが一目で分かるようにする */}
                    {(() => {
                      const count = optionDetails.filter((d) => d.name && d.name.trim() !== '').length;
                      return count > 0 ? (
                        <Chip
                          label={count}
                          size="small"
                          color="primary"
                          sx={{
                            height: 22,
                            minWidth: 22,
                            borderRadius: '50%',
                            fontWeight: 'bold',
                            '& .MuiChip-label': { px: 0 },
                          }}
                        />
                      ) : null;
                    })()}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {/* オプション詳細（管理者同様の自由入力。名前・キャストバック・収益） */}
                  <Typography variant="subtitle2" gutterBottom>
                    オプション詳細
                  </Typography>
                  {optionDetails.map((detail, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="オプション項目"
                            value={detail.name}
                            onChange={(e) => handleDetailChange(index, 'name', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="キャストバック"
                            value={detail.castback}
                            onChange={(e) => handleDetailChange(index, 'castback', e.target.value)}
                            InputProps={{ startAdornment: <span>¥</span> }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="収益"
                            value={detail.profit}
                            onChange={(e) => handleDetailChange(index, 'profit', e.target.value)}
                            InputProps={{ startAdornment: <span>¥</span> }}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: 'right' }}>
                          <IconButton color="error" size="small" onClick={() => handleRemoveDetail(index)}>
                            <CloseIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddDetail} sx={{ mb: 2 }}>
                    オプションを追加
                  </Button>

                  {/* 定義済みオプションからの選択 */}
                  {optionChoices.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }} gutterBottom>
                        オプション選択
                      </Typography>
                      <FormGroup>
                        {optionChoices.map((option) => (
                          <FormControlLabel
                            key={option.id}
                            control={
                              <Checkbox
                                checked={optionDetails.some((d) => d.name === option.name)}
                                onChange={(e) => handleOptionToggle(e.target.checked, option)}
                              />
                            }
                            label={`${option.name} (${option.price}円)`}
                          />
                        ))}
                      </FormGroup>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* キャストバック・総額（トグルメニューの下に配置・編集不可） */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="キャストバック"
                value={castback.toLocaleString()}
                InputProps={{ readOnly: true, startAdornment: <span>¥</span> }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="総額"
                value={grandTotal.toLocaleString()}
                InputProps={{ readOnly: true, startAdornment: <span>¥</span> }}
              />
            </Grid>

            {/* 入室時間（時刻入力 or 現在時刻ボタン） */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="入室時間"
                  type="time"
                  value={entryTime}
                  onChange={(e) => setEntryTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setEntryTime(format(new Date(), 'HH:mm'))}
                >
                  現在時刻
                </Button>
              </Stack>
            </Grid>

            {/* サービスタイム（追加時間・分）。退出時間 = 入室 + コース + サービスタイム */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="サービスタイム（追加時間）"
                value={serviceTime}
                onChange={(e) => setServiceTime(parseInt(e.target.value, 10) || 0)}
                InputProps={{ endAdornment: <span>分</span> }}
                helperText="退出時間は「入室時間 + コース時間 + サービスタイム」で計算されます"
              />
            </Grid>

            {/* 特記事項 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="特記事項"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" color="secondary" onClick={() => navigate('/orders')} disabled={saving}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </Stack>
      </Container>
    </DashboardLayout>
  );
};

export default CastOrderEdit;
